/**
 * Zone Consensus Metrics
 *
 * Calculates average/consensus behavior across all laps for a zone.
 * These represent the "typical" riding behavior in each zone.
 */

import { type ZoneConsensusMetrics } from './TrackZoneTypes'
import { type ZoneDataPoint } from './ZoneDetection'
import { average } from './mathUtils'
import { maxOf, minOf } from '../../utils/extremes'

/** Calculate consensus metrics for a zone from all laps */
export function calculateConsensusMetrics(
  zoneData: ZoneDataPoint[][]
): ZoneConsensusMetrics {
  const allEntryPoints = zoneData.map((d) => d[0]).filter(Boolean)
  const allExitPoints = zoneData.map((d) => d[d.length - 1]).filter(Boolean)
  const allPoints = zoneData.flat()

  const speedMetrics = calculateSpeedConsensus(
    allEntryPoints,
    allExitPoints,
    zoneData
  )
  const leanMetrics = calculateLeanConsensus(allPoints)
  const accelerationMetrics = calculateAccelerationConsensus(allPoints)

  return {
    ...speedMetrics,
    ...leanMetrics,
    ...accelerationMetrics,
  }
}

function calculateSpeedConsensus(
  entryPoints: ZoneDataPoint[],
  exitPoints: ZoneDataPoint[],
  zoneData: ZoneDataPoint[][]
) {
  return {
    avgEntrySpeed: average(entryPoints.map((p) => p.speed)),
    avgExitSpeed: average(exitPoints.map((p) => p.speed)),
    avgMinSpeed: average(zoneData.map((d) => minOf(d.map((p) => p.speed)))),
  }
}

function calculateLeanConsensus(allPoints: ZoneDataPoint[]) {
  const leanAngles = allPoints.map((p) => Math.abs(p.leanAngle))
  return {
    avgLeanAngle: average(leanAngles),
    peakLeanAngle: Math.max(maxOf(leanAngles), 0),
  }
}

function calculateAccelerationConsensus(allPoints: ZoneDataPoint[]) {
  // Acceleration is pre-smoothed at the lap level by analyzeTrackZones
  const allAccel = allPoints.map((p) => p.acceleration)

  const negativeAccel = allAccel.filter((a) => a < 0)
  const positiveAccel = allAccel.filter((a) => a > 0)

  return {
    avgDeceleration: negativeAccel.length > 0 ? average(negativeAccel) : 0,
    peakDeceleration: negativeAccel.length > 0 ? minOf(negativeAccel) : 0,
    avgAcceleration: positiveAccel.length > 0 ? average(positiveAccel) : 0,
  }
}
